/**
 * CoCeoVoiceBlock — narrator line for Inbox header and Strategy Insights intro.
 *
 * Authority: docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md §5.
 * Behaviour: renders deterministic fallback from copy template immediately;
 *            races the LLM fetch with a 1500ms timeout; crossfades to the
 *            LLM line on success; keeps the fallback on timeout/failure.
 * Never blocks render. Never raises to the UI.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { DS_COLORS, DS_SPACING, DS_TYPOGRAPHY, DS_MOTION } from '@/config/ui-invariants';

export function CoCeoVoiceBlock({ template, data }) {
  const [line, setLine] = useState(null);
  const [source, setSource] = useState('fallback');

  useEffect(() => {
    if (typeof template !== 'function') {
      setLine(null);
      return undefined;
    }
    const { fallback, fetch: fetchLine } = template(data || {});
    setLine(fallback);
    setSource('fallback');
    let cancelled = false;
    if (typeof fetchLine === 'function') {
      fetchLine()
        .then((llm) => {
          if (!cancelled && typeof llm === 'string' && llm.trim()) {
            setLine(llm);
            setSource('llm');
          }
        })
        .catch(() => {
          // swallow — fallback already rendered
        });
    }
    return () => {
      cancelled = true;
    };
  }, [template, data]);

  if (!line) return null;

  return (
    <div
      style={{
        paddingTop: DS_SPACING.labelToContent,
        paddingBottom: DS_SPACING.labelToContent,
        maxWidth: DS_SPACING.longReadMaxWidth,
      }}
    >
      <AnimatePresence mode="wait">
        <motion.p
          key={source}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DS_MOTION.regen.fadeMs / 1000, ease: 'easeOut' }}
          style={{
            ...DS_TYPOGRAPHY.bodyMd,
            color: DS_COLORS.text.primary,
            margin: 0,
          }}
        >
          {line}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

CoCeoVoiceBlock.propTypes = {
  template: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default CoCeoVoiceBlock;
